import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import { format, intlFormatDistance } from 'date-fns';
import { Typography, Box, Card, Icon } from '@kistech/kis-ui';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box as MuiBox,
  Grid
} from '@mui/material';
import { useActions } from 'hooks/useActions';
import { selectors as authSelectors } from 'store/auth';
import { selectors as clientsSelectors, Client } from 'store/clients';
import { selectors as kiosksSelectors, actions as kiosksActions, Kiosk } from 'store/kiosks';
import { formatTitle, capitalizeFirst } from 'utils/stringFormat';
import { useStyles, Tooltips } from '../styled';
import KioskActions from './KioskActions';
import KioskHardware from './KioskHardware';
import Popup from './Popup';

export interface KioskTableProps {
  pending: boolean;
  sortedKiosks: Kiosk[];
  setSortedKiosks: (kiosks: Kiosk[]) => void;
  sortParams: { column: string; order: string };
  setSortParams: (params: { column: string; order: string }) => void;
  message: string;
}

export const sort = (
  data: Kiosk[],
  stockCountsByPrinter: Map<string, Record<string, number>>,
  sortValue: string,
  sortDirection: string
): Kiosk[] => {
  const mapElement = stockCountsByPrinter.get(sortValue);

  return data.sort((a, b) => {
    let sortA: string | number = '';
    let sortB: string | number = '';

    // Determine if the sortValue is a Kiosk property, otherwise it is
    // a printer displayName and we need to sort by stock count
    if (['name', 'venueName', 'lastPrint', 'status'].includes(sortValue)) {
      // Sort by a Kiosk property
      const value: keyof Kiosk = sortValue as keyof Kiosk;
      sortA = (a[value] as string) || '';
      sortB = (b[value] as string) || '';

      // check to avoid case insensitive data and to handle date fields
      if (typeof sortA === 'string' && typeof sortB === 'string') {
        if (value === 'lastPrint') {
          sortA = sortA ? new Date(sortA).getTime() : 0;
          sortB = sortB ? new Date(sortB).getTime() : 0;
        } else if (value === 'status') {
          sortA = a.online ? sortA.toLowerCase() : 'offline';
          sortB = b.online ? sortB.toLowerCase() : 'offline';
        } else {
          sortA = sortA.toLowerCase();
          sortB = sortB.toLowerCase();
        }
      }
    } else {
      // Sort by the stock count of a printer (displayName)
      sortA = (mapElement && mapElement[a.id]) || 0;
      sortB = (mapElement && mapElement[b.id]) || 0;
    }

    if (sortA < sortB) {
      return sortDirection === 'asc' ? -1 : 1;
    } else if (sortA > sortB) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });
};

const KioskTable: React.FC<KioskTableProps> = (props: KioskTableProps) => {
  const { pending, sortedKiosks, setSortedKiosks, sortParams, setSortParams, message } = props;

  // ACTIONS AND SELECTORS
  const { getKiosks, resetMessage } = useActions({
    getKiosks: kiosksActions.getKiosks,
    resetMessage: kiosksActions.resetMessage
  });
  const admin = useSelector(authSelectors.selectAdmin);
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const kiosks = useSelector(kiosksSelectors.selectKiosks);
  const autoRefresh = useSelector(kiosksSelectors.selectAutoRefresh);
  const stockCountsByPrinter = useSelector(kiosksSelectors.selectStockCountsByPrinter);

  const clientRef = React.useRef(client.id);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const lastKioskAPIRef = React.useRef(0);

  const [tabVisible, setTabVisible] = React.useState(true);
  const [readOnlyAdmin, setReadOnlyAdmin] = React.useState(false);

  const classes = useStyles()();

  React.useEffect(() => {
    clientRef.current = '';
    timerRef.current = undefined;
    lastKioskAPIRef.current = 0;
    document.addEventListener('visibilitychange', handleVisibilityChange, true);

    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange, true);
    };
  }, []);

  // check for user is Read-Only Admin or not, if yes hide switch button and show text
  React.useEffect(() => {
    if (admin && admin.role === 'ReadOnlyAdmin') setReadOnlyAdmin(true);
  }, [admin]);

  React.useEffect(() => {
    if (!autoRefresh || clientRef.current !== client.id) {
      getKiosks({});
      clientRef.current = client.id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  // By default, data will be sorted based on name in ascending order
  React.useEffect(() => {
    if (kiosks) {
      const kiosksToShow = kiosks.filter((k: Kiosk) => k.configuration && k.configuration.showInKCC);
      const sortedData = sort(kiosksToShow, stockCountsByPrinter, sortParams.column, sortParams.order);
      setSortedKiosks(sortedData);
      lastKioskAPIRef.current = new Date().getTime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kiosks, sortParams]);

  React.useEffect(() => {
    if (autoRefresh && tabVisible) {
      if (lastKioskAPIRef.current + 30000 < new Date().getTime()) getKiosks({});
      timerRef.current = setInterval(() => {
        getKiosks({});
      }, 30000);
    } else {
      clearInterval(timerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, tabVisible]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  const handleVisibilityChange = () => {
    if (document.hidden) {
      setTabVisible(false);
    } else {
      setTabVisible(true);
    }
  };

  const handleHeaderClick = (key: string) => {
    if (sortParams.column === key) {
      const sortOrder = sortParams.order === 'asc' ? 'desc' : 'asc';
      setSortParams({ column: key, order: sortOrder });
    } else {
      setSortParams({ column: key, order: 'asc' }); // For new column, sort in asc order.
    }
  };

  const SortArrow = (sortArrowProps: { type: string }) => {
    return (
      <Box
        style={{ display: 'inline-block', cursor: 'pointer' }}
        onClick={() => handleHeaderClick(sortArrowProps.type)}
      >
        <Icon
          name={sortParams.order === 'asc' ? 'drop_up' : 'drop_down'}
          color="#2A2A2A"
          size={15}
          style={{ marginLeft: '5px' }}
        />
      </Box>
    );
  };

  // IF NO CLIENT GO TO CLIENT SELECTION PAGE
  if (!client) return <Redirect to="/clients" />;

  return (
    <>
      {message && message.length > 0 && !pending && <Popup message={message} resetMessage={resetMessage} />}
      {/* TABLE OF KIOSKS */}
      <MuiBox className={classes.kioskDetailsOuterBox}>
        <Card hasBorderRadius raised fullHeight>
          <Box flexOverride="1">
            <TableContainer className={classes.kioskDetailsTableContainer} id="kioskDetailsTable">
              <MuiTable stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      className={classes.kioskDetailsHeaders}
                      style={{ width: '220px', paddingLeft: '24px', whiteSpace: 'nowrap' }}
                      onClick={() => handleHeaderClick('name')}
                    >
                      Kiosk Name
                      {sortParams.column === 'name' && <SortArrow type={'name'} />}
                    </TableCell>
                    <TableCell
                      className={classes.kioskDetailsHeaders}
                      style={{ width: '200px', whiteSpace: 'nowrap' }}
                      onClick={() => handleHeaderClick('venueName')}
                    >
                      Location
                      {sortParams.column === 'venueName' && <SortArrow type={'venueName'} />}
                    </TableCell>
                    {stockCountsByPrinter.size > 0 &&
                      [...stockCountsByPrinter.keys()].map((key) => (
                        <TableCell
                          className={classes.kioskDetailsHeaders}
                          style={{ width: '160px', whiteSpace: 'nowrap' }}
                          onClick={() => handleHeaderClick(key)}
                        >
                          {key} Remaining
                          {sortParams.column === key && <SortArrow type={key} />}
                        </TableCell>
                      ))}
                    <TableCell
                      className={classes.kioskDetailsHeaders}
                      style={{ width: '160px', whiteSpace: 'nowrap' }}
                      onClick={() => handleHeaderClick('lastPrint')}
                    >
                      Last Delivery
                      {sortParams.column === 'lastPrint' && <SortArrow type={'lastPrint'} />}
                    </TableCell>
                    <TableCell
                      className={classes.kioskDetailsHeaders}
                      onClick={() => handleHeaderClick('status')}
                      style={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    >
                      Status
                      {sortParams.column === 'status' && <SortArrow type={'status'} />}
                    </TableCell>
                    {!readOnlyAdmin && (
                      <TableCell className={classes.kioskDetailsHeaders} style={{ cursor: 'default' }}>
                        Kiosk Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedKiosks &&
                    sortedKiosks.map((kioskDetailsObj: Kiosk) => (
                      <TableRow
                        key={kioskDetailsObj.id}
                        className={
                          kioskDetailsObj.status === 'Active' && kioskDetailsObj.online
                            ? classes.Active
                            : classes.Inactive
                        }
                      >
                        <TableCell className={classes.kioskDetailsKioskName}>
                          <Typography size={16}>{formatTitle(kioskDetailsObj.name)}</Typography>
                        </TableCell>
                        <TableCell className={classes.kioskDetailsCell}>
                          <Typography size={16}>{kioskDetailsObj.venueName}</Typography>
                        </TableCell>
                        {stockCountsByPrinter.size > 0 &&
                          [...stockCountsByPrinter.values()].map((value) => (
                            <TableCell className={classes.kioskDetailsCell}>
                              <Typography size={16}>{value[kioskDetailsObj.id] || '-'}</Typography>
                            </TableCell>
                          ))}
                        <TableCell className={classes.kioskDetailsCell}>
                          <Tooltips
                            title={
                              kioskDetailsObj.lastPrint
                                ? format(new Date(kioskDetailsObj.lastPrint), 'MM/dd/yyyy hh:mm:ss a')
                                : ''
                            }
                            arrow
                            placement={'top'}
                          >
                            <Grid style={{ cursor: kioskDetailsObj.lastPrint ? 'pointer' : 'auto' }}>
                              <Typography size={16}>
                                {kioskDetailsObj.lastPrint
                                  ? capitalizeFirst(intlFormatDistance(new Date(kioskDetailsObj.lastPrint), new Date()))
                                  : '-'}
                              </Typography>
                            </Grid>
                          </Tooltips>
                        </TableCell>
                        <TableCell>
                          <KioskHardware kiosk={kioskDetailsObj} />
                        </TableCell>
                        {!readOnlyAdmin && (
                          <TableCell>
                            <KioskActions kiosk={kioskDetailsObj} />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </MuiTable>
            </TableContainer>
          </Box>
        </Card>
      </MuiBox>
      <Grid className={classes.footer}>
        <p className={classes.total}>Kiosks: {sortedKiosks.length}</p>
      </Grid>
    </>
  );
};

export default KioskTable;
